/** One-click public-registry verification chips (GLEIF LEI, EU VIES VAT,
 *  domain RDAP health) for Customer / Vendor 360 headers. */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Globe, Loader2 } from "lucide-react";

const chip = "inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded-md border border-slate-300 bg-white hover:border-emerald-600 transition-colors";

export function VerifyChips({ name, vat, domain, testid = "verify-chips" }) {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const run = async (kind) => {
    setBusy(kind); setResult(null);
    try {
      let data, summary;
      if (kind === "company") {
        ({ data } = await api.get("/public-ref/gleif/verify", { params: { name } }));
        const hit = (data.matches || [])[0];
        summary = hit
          ? `GLEIF ${data.match_status}: ${hit.legal_name} · LEI ${hit.lei} · ${hit.registration_status || ""}`
          : "GLEIF: no LEI record found for this name";
      } else if (kind === "vat") {
        const v = String(vat).replace(/\s/g, "");
        ({ data } = await api.get("/public-ref/vies/verify",
          { params: { country: v.slice(0, 2), vat: v.slice(2) } }));
        summary = (data.result || data.valid) === true || data.result === "VALID"
          ? `VIES VALID — ${data.name || ""}`
          : `VIES: ${data.result || "NOT VALID"}`;
      } else {
        ({ data } = await api.get("/public-ref/rdap/domain", { params: { domain } }));
        const sm = data.summary || {};
        summary = `RDAP ${domain}: ${sm.registrar ? `registrar ${sm.registrar} · ` : ""}` +
          `${sm.expiration ? `expires ${String(sm.expiration).slice(0, 10)}` : data.source_status}`;
      }
      setResult({ kind, summary, raw: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    }
    setBusy("");
  };

  const spin = (k) => busy === k
    ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3 text-emerald-700" />;

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      {name && <button onClick={() => run("company")} disabled={!!busy}
          data-testid={`${testid}-company`} className={chip}>{spin("company")} Verify company</button>}
      {vat && <button onClick={() => run("vat")} disabled={!!busy}
          data-testid={`${testid}-vat`} className={chip}>{spin("vat")} Verify VAT</button>}
      {domain && <button onClick={() => run("domain")} disabled={!!busy}
          data-testid={`${testid}-domain`} className={chip}><Globe className="h-3 w-3 text-sky-700" /> Check domain</button>}
      {result && (
        <span data-testid={`${testid}-result`}
            className="text-[10.5px] px-2 py-1 rounded-md bg-slate-900 text-white max-w-md truncate">
          {result.summary}</span>)}
    </span>
  );
}
